import { useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronUp, ShieldAlert } from 'lucide-react';

export type SafetySeverity = 'info' | 'caution' | 'critical';

interface SafetyNoteProps {
  severity?: SafetySeverity;
  title: string;
  children: React.ReactNode;
  /** When true, the body starts collapsed and the user taps the header to expand.
      Defaults to false — safety notes are visually loud by default. */
  defaultCollapsed?: boolean;
  /** Optional dismissibility for situational notes. Off by default. */
  dismissible?: boolean;
}

/**
 * v129 — Shared safety note component.
 *
 * Visual pattern matches existing InfoCards but with a thicker, higher-contrast
 * ember stripe (and rose stripe for critical) so the user visually clocks this
 * as a safety surface, not a hint or tip.
 *
 * Mobile rules (v129):
 *   - Header is a full-width tap target (≥44px touch height when collapsible).
 *   - Dismiss button is ≥44×44 px touch target.
 *   - No hover-only state; tap to expand/collapse.
 */
export function SafetyNote({
  severity = 'caution',
  title,
  children,
  defaultCollapsed = false,
  dismissible = false,
}: SafetyNoteProps) {
  const [open, setOpen] = useState(!defaultCollapsed);
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return null;

  const palette = paletteFor(severity);
  const Icon = severity === 'critical' ? ShieldAlert : AlertTriangle;

  return (
    <div
      className={`relative rounded-md border ${palette.border} ${palette.bg} my-3 overflow-hidden`}
      role="alert"
      aria-live="polite"
      data-safety-severity={severity}
    >
      {/* Stripe accent on the left edge */}
      <div className={`absolute left-0 top-0 bottom-0 w-1 ${palette.stripe}`} />

      <button
        onClick={() => setOpen(!open)}
        className={`w-full flex items-center gap-2 px-3 py-3 text-left min-h-[44px] ${palette.headerHover}`}
        aria-expanded={open}
      >
        <Icon size={16} className={`shrink-0 ${palette.icon}`} />
        <span className={`text-sm font-semibold flex-1 ${palette.title}`}>{title}</span>
        {open
          ? <ChevronUp size={14} className="text-zinc-500 shrink-0" />
          : <ChevronDown size={14} className="text-zinc-500 shrink-0" />}
      </button>

      {open && (
        <div className="px-3 pb-3 -mt-1 text-sm text-zinc-300 leading-relaxed">
          {children}
        </div>
      )}

      {dismissible && open && (
        <button
          onClick={() => setDismissed(true)}
          className="absolute top-1 right-1 w-11 h-11 flex items-center justify-center text-zinc-500 hover:text-zinc-300 text-xs font-mono"
          aria-label="Dismiss safety note"
        >
          ✕
        </button>
      )}
    </div>
  );
}

function paletteFor(severity: SafetySeverity) {
  if (severity === 'critical') {
    return {
      border: 'border-rose-500/40',
      bg: 'bg-rose-500/5',
      stripe: 'bg-rose-500',
      icon: 'text-rose-400',
      title: 'text-rose-200',
      headerHover: 'hover:bg-rose-500/8',
    };
  }
  if (severity === 'caution') {
    return {
      border: 'border-ember-500/40',
      bg: 'bg-ember-500/5',
      stripe: 'bg-ember-500',
      icon: 'text-ember-400',
      title: 'text-ember-200',
      headerHover: 'hover:bg-ember-500/8',
    };
  }
  // info
  return {
    border: 'border-cyan-500/30',
    bg: 'bg-cyan-500/5',
    stripe: 'bg-cyan-500',
    icon: 'text-cyan-400',
    title: 'text-cyan-200',
    headerHover: 'hover:bg-cyan-500/8',
  };
}
